import { extractJsonObject } from "../outline/outlinePrompt.js";
import { arrangeSlideText, detectLayerIssues } from "../layout/autoArrange.js";
import { effectiveThemeForSlide } from "../layout/compositionLayout.js";
import { fitTextSizeRel } from "../editor/fitText.js";
import { applyTextCase, resolveTextStyle, setTextOverride } from "../editor/textStyle.js";
import { updateSlideInDeck } from "../editor/deckEditor.js";
import { bedrockConverseWithImage } from "./bedrockVision.js";
const AUTOFIT_SYSTEM = `You critique a rendered presentation slide composite (background + text).
Return ONLY JSON:
{"layers":[{"id":"<layerId>","issues":["overlap"|"clip"|"lowContrast"]}],"freeZones":[{"x":0,"y":0,"w":0,"h":0}]}
Do NOT return absolute coordinates for layers. Only issue tags and freeZones for calm text areas.`;
function normalizeDiagnosis(raw) {
    const obj = extractJsonObject(raw);
    if (!obj)
        throw new Error("Autofit: risposta non JSON.");
    const layers = (Array.isArray(obj.layers) ? obj.layers : [])
        .map((item) => {
        if (!item || typeof item !== "object")
            return null;
        const o = item;
        const id = String(o.id ?? "").trim();
        if (!id)
            return null;
        const issues = (Array.isArray(o.issues) ? o.issues : [])
            .map((x) => String(x).trim())
            .filter(Boolean);
        return { id, issues };
    })
        .filter((x) => x !== null);
    const freeZones = (Array.isArray(obj.freeZones) ? obj.freeZones : [])
        .map((z) => {
        if (!z || typeof z !== "object")
            return null;
        const o = z;
        const x = Number(o.x);
        const y = Number(o.y);
        const w = Number(o.w);
        const h = Number(o.h);
        if (![x, y, w, h].every(Number.isFinite))
            return null;
        return { x, y, w, h };
    })
        .filter((z) => z !== null && z.w > 0.05 && z.h > 0.05);
    return { layers, freeZones };
}
export async function diagnoseSlideAutofit(cfg, compositeDataUrl, layerMeta) {
    const raw = await bedrockConverseWithImage(cfg, {
        feature: "surf.slide.autofit",
        systemText: AUTOFIT_SYSTEM,
        userText: `Current text layers (normalized rects, sizeRel):\n${JSON.stringify(layerMeta, null, 2)}`,
        imageDataUrl: compositeDataUrl,
    });
    return normalizeDiagnosis(raw);
}
function issueScore(slide, theme) {
    const slideTheme = effectiveThemeForSlide(theme, slide);
    return detectLayerIssues(slide, slideTheme).length;
}
function applyAutofitAct(slide, theme, diagnosis) {
    let next = slide;
    if (diagnosis.freeZones.length) {
        next = {
            ...next,
            background: { ...next.background, freeZones: diagnosis.freeZones },
        };
    }
    const slideTheme = effectiveThemeForSlide(theme, next);
    next = arrangeSlideText(next, slideTheme);
    const lowContrastIds = new Set(diagnosis.layers.filter((l) => l.issues.includes("lowContrast")).map((l) => l.id));
    const clipIds = new Set(diagnosis.layers.filter((l) => l.issues.includes("clip")).map((l) => l.id));
    const layers = next.layers.map((layer) => {
        if (layer.type !== "text")
            return layer;
        const ts = resolveTextStyle(layer, slideTheme);
        let patched = layer;
        if (clipIds.has(layer.id)) {
            const fitted = fitTextSizeRel(applyTextCase(layer.content, ts.case), layer, ts.sizeRel, ts.lineHeight, ts.maxLines, undefined, ts.font, ts.weight);
            if (fitted < ts.sizeRel) {
                patched = setTextOverride(patched, { sizeRel: fitted });
            }
        }
        if (lowContrastIds.has(layer.id) && slideTheme.legibility.strategy !== "scrim") {
            patched = {
                ...patched,
                style: { ...patched.style, scrimHint: "soft" },
            };
        }
        return patched;
    });
    return { ...next, layers };
}
/** On-demand autofit: max 2 vision iterations, apply only if score improves. */
export async function runSlideAutofit(cfg, deck, slideIndex, rasterize) {
    const slide = deck.slides[slideIndex];
    if (!slide)
        throw new Error("Slide non trovata.");
    const theme = deck.theme;
    const beforeScore = issueScore(slide, theme);
    let workingSlide = slide;
    let bestSlide = slide;
    let bestScore = beforeScore;
    const layerMeta = () => workingSlide.layers
        .filter((l) => l.type === "text")
        .map((l) => {
        const ts = resolveTextStyle(l, theme);
        return {
            id: l.id,
            rect: { x: l.x, y: l.y, w: l.w, h: l.h },
            sizeRel: ts.sizeRel,
            textPreview: l.content.slice(0, 120),
        };
    });
    for (let iter = 0; iter < 2; iter += 1) {
        const dataUrl = await rasterize(workingSlide, theme);
        const diagnosis = await diagnoseSlideAutofit(cfg, dataUrl, layerMeta());
        const candidate = applyAutofitAct(workingSlide, theme, diagnosis);
        const candidateTheme = effectiveThemeForSlide(theme, candidate);
        const score = issueScore(candidate, candidateTheme);
        if (score < bestScore) {
            bestScore = score;
            bestSlide = candidate;
        }
        workingSlide = candidate;
        if (score === 0)
            break;
    }
    const applied = bestScore < beforeScore;
    const nextDeck = applied
        ? updateSlideInDeck(deck, slide.id, () => bestSlide)
        : deck;
    return {
        deck: nextDeck,
        applied,
        beforeScore,
        afterScore: bestScore,
    };
}
